/* Daymint · oc-offer.js (COMMERCE). Offer cards, PDP gallery / home stage, sticky bar, cart page.
   Needs assets/oc.js (window.OC, CONTRACT §6). No dependencies. Loaded with `defer` by the COMMERCE sections.
   Pure maths (compute, parseInterval, pct, perBottle, planInputValue, fmt) is exported for Node tests
   (qa/offer-harness.mjs): it mirrors snippets/oc-offer.liquid exactly (cents, CONTRACT §8).
   v1.1 (FIX round): one shared gate in Liquid (oc-live), selling_plan / _purchase_mode inputs enabled only when they apply,
   bfcache reset, Shopify's own add/change errors, sticky phases (PDP: "See options" before the options), editor cleanup.
   Events emitted: oc:variant-change {form, card, variantId, planId, price, perBottle, bottles, mode, tokens, orderable, user}
                   oc:mode-change {form, mode, card}
   Analytics (Shopify customer-events bus via OC.track): daymint_offer_selected, daymint_add_to_cart_option. */
(function (root, factory) {
  var api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    if (window.OCOffer && window.OCOffer.booted) return; // several sections include this file
    window.OCOffer = api;
    api.boot();
  }
})(this, function () {
  'use strict';

  /* ======================= pure maths (no DOM) ======================= */
  var CARDS = ['single', 'bundle', 'subscription'];

  /** Percentage of `save` against `ref`: 'nearest' (PRICING-V3, 22%) or 'down' (COPY-DECK-v2, 21%). Same float ops as Liquid. */
  function pct(save, ref, mode) {
    if (!(ref > 0)) return 0;
    var x = (save * 100.0) / ref;
    return mode === 'down' ? Math.floor(x) : Math.round(x);
  }
  /** cents ÷ bottles, rounded to the cent (Liquid: price | times: 1.0 | divided_by: n | round). */
  function perBottle(price, n) { return Math.round((price * 1.0) / (n || 1)); }
  function toInt(s) { s = String(s == null ? '' : s).trim(); return /^-?\d+$/.test(s) ? parseInt(s, 10) : 0; }

  /** Interval words from a selling plan's option values: "1 month(s)" → "every month", "30 days" → "every 30 days". */
  function parseInterval(options, fallback) {
    var ov = (' ' + (options || []).join(' ')).toLowerCase().split('(s)').join('s').replace(/-/g, ' ').replace(/,/g, ' ').trim();
    var words = ov ? ov.split(/\s+/) : [];
    var prev = '';
    for (var i = 0; i < words.length; i++) {
      var w = words[i], unit = '';
      if (w.indexOf('day') > -1) unit = 'day';
      else if (w.indexOf('week') > -1) unit = 'week';
      else if (w.indexOf('month') > -1) unit = 'month';
      else if (w.indexOf('year') > -1 || w.indexOf('annual') > -1) unit = 'year';
      if (unit) {
        var n = toInt(prev);
        return n > 1 ? 'every ' + n + ' ' + unit + 's' : 'every ' + unit;
      }
      prev = w;
    }
    return fallback || '';
  }

  /** Fill %tokens%; null when a token is missing or empty (the caller hides the element). Same rule as OC.fmt. */
  function fmt(template, tokens) {
    var missing = false;
    var out = String(template == null ? '' : template).replace(/%([a-z0-9_]+)%/g, function (m, k) {
      var v = tokens ? tokens[k] : undefined;
      if (v === undefined || v === null || v === '') { missing = true; return ''; }
      return String(v);
    });
    return missing ? null : out;
  }

  function defaultMoney(c) { return '$' + (Number(c) / 100).toFixed(2); }
  function extend(a) { for (var i = 1; i < arguments.length; i++) { var b = arguments[i]; for (var k in b) if (Object.prototype.hasOwnProperty.call(b, k)) a[k] = b[k]; } return a; }

  /**
   * Everything the offer shows, from live Shopify data (the JSON oc-offer.liquid embeds).
   * data: { bottles:{single,bundle}, single:{id,price,available,tracked,sourcingOk,title}|null, bundle:{…}|null,
   *         plan:{id,name,price,renewal,options[]}|null, subReady, orderable, savings, pctRound, defaultCard,
   *         interval, labels:{one,many}, titles:{single,bundle,subscription} }
   */
  function compute(data, money) {
    money = money || defaultMoney;
    var M = function (c) { return money(c); };
    var nB = (data.bottles && data.bottles.bundle) || 3;
    var S = data.single || null, B = data.bundle || null, P = data.plan || null;
    var mode = data.pctRound === 'down' ? 'down' : 'nearest';
    var labels = data.labels || {}, titles = data.titles || {};
    var interval = (P && parseInterval(P.options, '')) || data.interval || '';

    var ps = S ? S.price : 0, pb = B ? B.price : 0;
    var pa = P ? P.price : 0, pr = P ? (P.renewal != null ? P.renewal : P.price) : 0;
    var cmpOk = !!P && pa === pr;
    var pbs = ps, pbb = perBottle(pb, nB), pbr = perBottle(pr, nB);
    var ref = ps * nB;
    var bsave = 0, bpct = 0, rsave = 0, rpct = 0, rvsb = 0, rvsbpct = 0;
    if (ref > 0) {
      bsave = ref - pb; bpct = pct(bsave, ref, mode);
      if (P) { rsave = ref - pr; rpct = pct(rsave, ref, mode); }
    }
    if (P && pb > 0) { rvsb = pb - pr; rvsbpct = pct(rvsb, pb, mode); }

    var savOn = data.savings !== false;
    var singleAv = !!(S && S.available), bundleAv = !!(B && B.available);
    var singleCmp = singleAv && (!!S.tracked || S.sourcingOk !== false);
    var bundleCmp = bundleAv && (!!B.tracked || B.sourcingOk !== false);
    var subReady = !!(data.subReady && P && B);

    var g = {
      bundleVsSingles: savOn && singleCmp && !!B && bsave > 0,
      refillVsSingles: savOn && singleCmp && cmpOk && rsave > 0,
      refillVsBundle: savOn && bundleCmp && cmpOk && rvsb > 0,
      refillCompare: cmpOk
    };
    g.refillBadge = subReady && g.refillVsSingles && g.refillVsBundle && pbr < pbs && pbr < pbb;
    g.h2 = subReady ? 'live' : (g.bundleVsSingles && pbb < pbs ? 'save' : 'flat');

    var common = {
      single_price: S ? M(ps) : '', bundle_price: B ? M(pb) : '',
      plan_price: P ? M(pa) : '', renewal_price: P ? M(pr) : '',
      single_per_bottle: S ? M(pbs) : '', bundle_per_bottle: B ? M(pbb) : '', plan_per_bottle: P ? M(pbr) : '',
      interval: interval, plan_name: P ? P.name : ''
    };
    var cards = { single: null, bundle: null, subscription: null };
    if (S) cards.single = {
      card: 'single', mode: 'once', variantId: S.id, planId: null, available: singleAv, selectable: singleAv,
      price: ps, perBottle: pbs, bottles: 1,
      tokens: extend({}, common, { price: M(ps), per_bottle: M(pbs), bottles: '1', bottles_label: labels.one || '', card_title: titles.single || '', variant_title: S.title || '' })
    };
    if (B) cards.bundle = {
      card: 'bundle', mode: 'once', variantId: B.id, planId: null, available: bundleAv, selectable: bundleAv,
      price: pb, perBottle: pbb, bottles: nB,
      tokens: extend({}, common, {
        price: M(pb), per_bottle: M(pbb), bottles: String(nB), bottles_label: labels.many || '', card_title: titles.bundle || '', variant_title: B.title || '',
        save_amount: g.bundleVsSingles ? M(bsave) : '', save_pct: g.bundleVsSingles ? bpct + '%' : '',
        singles_total: g.bundleVsSingles ? M(ref) : '' // 3 x the live single price (sticky strike-through, Jaxon 2026-10-20)
      })
    };
    if (P && B) cards.subscription = {
      card: 'subscription', mode: 'sub', variantId: B.id, planId: P.id, available: bundleAv, selectable: subReady && bundleAv,
      price: pa, perBottle: pbr, bottles: nB,
      tokens: extend({}, common, {
        price: M(pa), per_bottle: M(pbr), bottles: String(nB), bottles_label: labels.many || '', card_title: titles.subscription || '', variant_title: B.title || '',
        save_amount: g.refillVsSingles ? M(rsave) : '', save_pct: g.refillVsSingles ? rpct + '%' : '',
        singles_total: g.refillVsSingles ? M(ref) : '',
        save_vs_bundle_amount: g.refillVsBundle ? M(rvsb) : '', save_vs_bundle_pct: g.refillVsBundle ? rvsbpct + '%' : '',
        plan_save_amount: g.refillVsBundle ? M(rvsb) : '', plan_save_pct: g.refillVsBundle ? rvsbpct + '%' : ''
      })
    };

    var def = data.defaultCard === 'single' ? 'single' : 'bundle'; // the subscription is never a default
    if (def === 'bundle' && !bundleAv) def = 'single';
    if (def === 'single' && !singleAv) def = bundleAv ? 'bundle' : '';

    return {
      cards: cards, gates: g, defaultCard: def, interval: interval, subReady: subReady, orderable: data.orderable !== false,
      plan: P, nums: { ref: ref, bsave: bsave, bpct: bpct, rsave: rsave, rpct: rpct, rvsb: rvsb, rvsbpct: rvsbpct, pbs: pbs, pbb: pbb, pbr: pbr, pa: pa, pr: pr }
    };
  }

  /** The hidden selling_plan input: the plan id ONLY for the subscription card while it can be sold; otherwise ''. */
  function planInputValue(model, card) {
    var c = model && model.cards && model.cards[card];
    if (card === 'subscription' && c && c.selectable && model.plan && model.plan.id) return String(model.plan.id);
    return '';
  }

  /* ======================= DOM ======================= */
  // Shopify's refusal texts that state a quantity ("You can only add 5 …", "All 5 … are in your cart"): a stock count,
  // which this site never shows. Product names with digits ("3-Bottle Bundle") don't match.
  var STOCK_RE = /(only\s+(add\s+)?|all\s+)\d+|\d+\s+(left|in stock|available)/i;
  var d = typeof document !== 'undefined' ? document : null;
  function $$(sel, rootEl) { return Array.prototype.slice.call((rootEl || d).querySelectorAll(sel)); }
  function OCx() { return window.OC || null; }
  function emit(name, detail, target) { var O = OCx(); if (O) return O.emit(name, detail, target); }
  function track(name, data) { var O = OCx(); if (O) O.track(name, data); }
  function announce(msg) { var O = OCx(); if (O && msg) O.a11y.announce(msg); }
  function reduced() { var O = OCx(); return !!(O && O.reducedMotion()); }
  var decoder = null;
  function decode(str) { if (!str || str.indexOf('&') < 0) return str || ''; decoder = decoder || d.createElement('textarea'); decoder.innerHTML = str; return decoder.value; }
  function showsCard(el, card) { return (' ' + (el.getAttribute('data-oc-show') || '') + ' ').indexOf(' ' + card + ' ') > -1; }

  /* ---------- offer ---------- */
  function initOffer(rootEl) {
    if (rootEl.__ocOffer) return;
    var dataEl = rootEl.querySelector('[data-oc-offer-data]');
    var form = rootEl.querySelector('form[data-oc-offer-form]');
    if (!dataEl || !form) return;
    var data;
    try { data = JSON.parse(dataEl.textContent); } catch (e) { return; }
    rootEl.__ocOffer = true;
    var O = OCx();
    var model = compute(data, O ? O.money : null);
    var planIn = form.querySelector('[data-oc-plan-input]');
    var modeIn = form.querySelector('[data-oc-mode-input]');
    var cta = form.querySelector('[data-oc-offer-cta]:not([data-oc-not-orderable])');
    var ctaLabel = cta && cta.querySelector('[data-oc-cta-label]');
    var ctaPrice = cta && cta.querySelector('[data-oc-cta-price]');
    var err = rootEl.querySelector('[data-oc-offer-error]');
    var errText = rootEl.querySelector('[data-oc-offer-error-text]');
    var added = rootEl.querySelector('[data-oc-offer-added]');
    var hint = rootEl.querySelector('[data-oc-choose-hint]');
    var byCard = {};
    $$('[data-oc-card-input]', form).forEach(function (i) { byCard[i.getAttribute('data-oc-card-input')] = i; });
    var state = { card: '', mode: '', busy: false };

    // The subscription radio ships disabled (no JS = no way to set selling_plan). Enable it only now, and only when it can be sold.
    var subIn = byCard.subscription;
    if (subIn) {
      var subOk = planIn && model.cards.subscription && model.cards.subscription.selectable && !subIn.hasAttribute('data-oc-na');
      subIn.disabled = !subOk;
      subIn.removeAttribute('data-oc-sub-pending');
    }

    function showErr(msg) { if (!err) return; if (errText) errText.textContent = msg || data.ui.error || ''; err.hidden = false; }
    /** Shopify's own reason when it refused the add (422, e.g. "… is already sold out"), else our generic line.
        A reason that names a quantity ("You can only add 5 …") would show a stock count: the limit line replaces it. */
    function addErrText(e, fallback) {
      var m = e && e.status === 422 && e.message ? String(e.message) : '';
      if (m && STOCK_RE.test(m)) m = data.ui.limitError || '';
      return m || fallback || data.ui.error;
    }
    function hideErr() { if (err) err.hidden = true; if (added) added.hidden = true; }

    function select(card, user) {
      var c = model.cards[card] || null;
      if (!c) card = '';
      state.card = card;
      var mode = c ? c.mode : 'once';
      rootEl.setAttribute('data-oc-sel', card);
      form.setAttribute('data-oc-card', card);
      form.setAttribute('data-oc-mode', mode);
      if (planIn) {
        // the plan field takes part in a submission ONLY while the sellable subscription card is selected
        planIn.value = planInputValue(model, card);
        planIn.disabled = !planIn.value;
      }
      if (modeIn) { modeIn.value = card || ''; modeIn.disabled = !card; }
      $$('[data-oc-show]', rootEl).forEach(function (el) { el.hidden = !card || !showsCard(el, card); });
      var inp = byCard[card];
      if (cta && ctaLabel) {
        if (inp) {
          ctaLabel.textContent = inp.getAttribute('data-oc-cta-label') || '';
          var pr = inp.getAttribute('data-oc-cta-price') || '';
          if (ctaPrice) { ctaPrice.textContent = pr; ctaPrice.hidden = !pr; }
        }
        cta.disabled = !model.orderable || !c || !c.selectable;
        if (card === 'subscription') cta.setAttribute('aria-describedby', 'oc-terms-' + data.uid);
        else cta.removeAttribute('aria-describedby');
      }
      if (hint) hint.hidden = !!card;
      if (O) O.a11y.syncChoices(form);
      // Liquid and JS must agree on the price: flag a mismatch in the theme editor only.
      if (inp && c && O && O.cfg && O.cfg.designMode && Number(inp.getAttribute('data-oc-price')) !== c.price && window.console) {
        console.warn('[oc-offer] price mismatch for ' + card + ': Liquid ' + inp.getAttribute('data-oc-price') + ' vs JS ' + c.price);
      }
      var detail = {
        form: form, card: card, variantId: c ? c.variantId : null, planId: c && c.mode === 'sub' ? c.planId : null,
        price: c ? c.price : null, perBottle: c ? c.perBottle : null, bottles: c ? c.bottles : null, mode: mode,
        tokens: c ? c.tokens : {}, orderable: model.orderable, user: !!user
      };
      emit('oc:variant-change', detail, form);
      if (mode !== state.mode) { state.mode = mode; emit('oc:mode-change', { form: form, mode: mode, card: card }, form); }
      if (user && c) {
        hideErr();
        track('daymint_offer_selected', { card: card, variant_id: c.variantId, plan_id: detail.planId, price: c.price / 100, currency: (O && O.cfg && O.cfg.currency) || '' });
        // no announce here: the radio's own name is spoken on selection (a live-region echo would say it twice)
      }
    }

    form.addEventListener('change', function (e) {
      var t = e.target;
      if (t && t.hasAttribute && t.hasAttribute('data-oc-card-input') && t.checked) {
        var picked = t.getAttribute('data-oc-card-input');
        select(picked, true);
        // Review v5 M1: two offer modules on one page (PDP #buy + #offer) stay on the same choice, so the sticky bar
        // (bound to #buy) always adds what she picked last. A synced subscription shows its terms beside that button too.
        $$('[data-oc-offer]').forEach(function (o) { if (o !== rootEl && o.__ocSync) o.__ocSync(picked); });
      }
    });

    form.addEventListener('submit', function (e) {
      var O2 = OCx();
      if (!O2) return; // let the native form post work
      e.preventDefault();
      if (state.busy) return;
      var card = state.card, c = model.cards[card];
      if (!card || !c) {
        if (hint) hint.hidden = false;
        var first = $$('[data-oc-card-input]:not(:disabled)', form)[0];
        if (first) first.focus();
        return;
      }
      if (!model.orderable || !c.selectable) return;
      var item = { id: c.variantId, quantity: 1, properties: { _purchase_mode: card } };
      if (card === 'subscription') {
        var pid = planInputValue(model, card);
        // Never add a one-time item while the subscription card is selected.
        if (!pid || !planIn || planIn.value !== pid) { showErr(data.ui.subError || data.ui.error); return; }
        item.selling_plan = Number(pid);
      }
      var sub = e.submitter && e.submitter !== cta && e.submitter.matches && e.submitter.matches('[data-oc-sticky-btn]') ? e.submitter : null;
      state.busy = true;
      hideErr();
      O2.a11y.busy(cta, true, data.ui.busy || undefined);
      if (sub) O2.a11y.busy(sub, true, data.ui.busy || undefined);
      O2.cart.add(item).then(function () {
        track('daymint_add_to_cart_option', { card: card, variant_id: c.variantId, plan_id: item.selling_plan || null });
        if (data.afterAdd === 'stay') {
          state.busy = false;
          O2.a11y.busy(cta, false);
          if (sub) O2.a11y.busy(sub, false);
          if (added) { added.hidden = false; announce(added.textContent.replace(/\s+/g, ' ').trim()); }
        } else {
          window.location.href = data.cartUrl || '/cart';
        }
      }).catch(function (er) {
        state.busy = false;
        O2.a11y.busy(cta, false);
        if (sub) O2.a11y.busy(sub, false);
        // a refused subscription add keeps its own message (Shopify's selling-plan errors are technical)
        showErr(card === 'subscription' && data.ui.subError ? data.ui.subError : addErrText(er, data.ui.error));
      });
    });

    // Back from /cart: browsers restore this page from the back/forward cache with the button still "Adding…".
    function onPageShow(e) {
      if (!e.persisted) return;
      state.busy = false;
      var O3 = OCx();
      if (O3) {
        O3.a11y.busy(cta, false);
        $$('[data-oc-sticky-btn][aria-busy="true"]').forEach(function (b) { O3.a11y.busy(b, false); });
      }
      hideErr();
      var cur = $$('[data-oc-card-input]', form).filter(function (i) { return i.checked && !i.disabled; })[0];
      var c0 = cur ? cur.getAttribute('data-oc-card-input') : model.defaultCard;
      if (c0 === 'subscription' && !(model.cards.subscription && model.cards.subscription.selectable)) c0 = model.defaultCard;
      select(c0, false);
    }
    window.addEventListener('pageshow', onPageShow);
    rootEl.__ocOff = function () { window.removeEventListener('pageshow', onPageShow); rootEl.__ocOffer = false; };

    var checked = $$('[data-oc-card-input]', form).filter(function (i) { return i.checked && !i.disabled; })[0];
    var start = checked ? checked.getAttribute('data-oc-card-input') : model.defaultCard;
    if (start === 'subscription') start = model.defaultCard; // bfcache/autofill must never restore a subscription silently
    if (start && byCard[start] && !byCard[start].checked) byCard[start].checked = true;
    if (start === model.defaultCard && byCard.subscription) byCard.subscription.checked = false;
    select(start, false);
    rootEl.__ocSelect = select;
    rootEl.__ocSync = function (card) { var i = byCard[card]; if (!i || i.disabled || i.checked) return; i.checked = true; select(card, false); };
    // Warm the other quantity image (hidden, so lazy-loading would wait until the first switch).
    setTimeout(function () {
      $$('[data-oc-for="' + form.getAttribute('id') + '"] [data-oc-vshow][hidden] img[loading="lazy"]').forEach(function (img) { img.loading = 'eager'; });
    }, 1500);
  }

  /* ---------- variant images: PDP gallery slide 1 and the homepage stage ---------- */
  function variantImages(e) {
    var det = e.detail || {};
    var fid = det.form && det.form.getAttribute('id'); // never form.id: the radios are named "id" and clobber it
    if (!fid) return;
    var want = det.card === 'single' ? 'single' : 'bundle';
    $$('[data-oc-for="' + fid + '"]').forEach(function (box) {
      var changed = false;
      $$('[data-oc-vshow]', box).forEach(function (el) {
        var h = el.getAttribute('data-oc-vshow') !== want;
        if (el.hidden !== h) { el.hidden = h; changed = true; }
      });
      if (changed && det.user && box.__ocGo) box.__ocGo(0);
    });
  }

  function initGallery(g) {
    if (g.__ocGallery) return;
    var track = g.querySelector('[data-oc-track]');
    if (!track) return;
    g.__ocGallery = true;
    var slides = $$('[data-oc-slide]', track);
    var thumbs = $$('[data-oc-thumb]', g);
    var dots = $$('[data-oc-dot]', g);
    var count = g.querySelector('[data-oc-count]');
    var tpl = count ? count.getAttribute('data-oc-count-tpl') || '' : '';
    var cur = 0;
    function mark(i) {
      cur = i;
      thumbs.forEach(function (t, k) { if (k === i) t.setAttribute('aria-current', 'true'); else t.removeAttribute('aria-current'); });
      dots.forEach(function (t, k) { if (k === i) t.setAttribute('aria-current', 'true'); else t.removeAttribute('aria-current'); });
      if (count && tpl) count.textContent = tpl.replace('{n}', i + 1).replace('{total}', slides.length);
    }
    function go(i) {
      var s = slides[i];
      if (!s) return;
      track.scrollTo({ left: s.offsetLeft, behavior: reduced() ? 'auto' : 'smooth' }); // track is position:relative
      mark(i);
    }
    g.__ocGo = go;
    thumbs.concat(dots).forEach(function (b) {
      b.addEventListener('click', function () { go(Number(b.getAttribute('data-oc-index')) || 0); });
    });
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) { if (en.isIntersecting && en.intersectionRatio >= 0.6) mark(slides.indexOf(en.target)); });
      }, { root: track, threshold: [0.6] });
      slides.forEach(function (s) { io.observe(s); });
    }
    mark(0);
  }

  /* ---------- sticky buy bar (all widths; Jaxon 2026-10-20: 'always an add to cart button at the bottom, like HotShift') ---------- */
  function qs(sel, fallback) { if (!sel) return fallback || null; try { return d.querySelector(sel); } catch (e) { return fallback || null; } }
  function validSel(sel, fallback) { if (!sel) return fallback; try { d.querySelector(sel); return sel; } catch (e) { return fallback; } }

  function initSticky(bar) {
    if (bar.__ocSticky) return;
    var O = OCx();
    var dataEl = bar.querySelector('[data-oc-sticky-data]');
    if (!O || !dataEl) return;
    var cfg;
    try { cfg = JSON.parse(dataEl.textContent); } catch (e) { return; }
    ['btnOnce', 'btnRefill', 'btnHome', 'save'].forEach(function (k) { cfg[k] = decode(cfg[k]); });
    if (cfg.line2) CARDS.forEach(function (k) { cfg.line2[k] = decode(cfg.line2[k]); });
    var pdpForm = d.querySelector('form[data-oc-offer-form][data-oc-context="pdp"]');
    var homeForm = d.querySelector('#offer form[data-oc-offer-form]') || d.querySelector('form[data-oc-offer-form][data-oc-context="home"]');
    var mode = cfg.mode === 'pdp' || cfg.mode === 'home' ? cfg.mode : (pdpForm ? 'pdp' : 'home');
    var form = mode === 'pdp' ? pdpForm : homeForm;
    if (mode === 'pdp' && !form) return;
    bar.__ocSticky = true;
    var offs = [];
    var line2 = bar.querySelector('[data-oc-sticky-line2]');
    // Jaxon 2026-10-20: centered deal line = name · struck 3-singles total · price (+ interval) · saving.
    var deal = bar.querySelector('[data-oc-sticky-deal]');
    var wasEl = bar.querySelector('[data-oc-sticky-was]'), wasV = bar.querySelector('[data-oc-sticky-was-v]');
    var priceEl = bar.querySelector('[data-oc-sticky-price]'), intEl = bar.querySelector('[data-oc-sticky-int]');
    var saveEl = bar.querySelector('[data-oc-sticky-save]');
    var btn = bar.querySelector('[data-oc-sticky-btn]');
    var btnLabel = btn && btn.querySelector('.oc-btn__label');
    var mq = window.matchMedia('(max-width: 1023.98px)');
    // past: the trigger (PDP main button / home first section) is above the viewport. early (PDP): the gallery has
    // scrolled away but the options are still below. offer: the cards or the button are on screen (the bar hides).
    var st = { past: false, early: false, offer: false, inView: false, blocked: false, card: '', orderable: true, tokens: {}, shown: false };

    // The thumbnail follows the selected quantity: variantImages() swaps [data-oc-vshow] inside [data-oc-for=<form id>].
    var thumbs = bar.querySelector('[data-oc-sticky-thumbs]');
    if (thumbs && form && form.getAttribute('id')) thumbs.setAttribute('data-oc-for', form.getAttribute('id'));

    function active() { return st.past || st.early; }
    function render() {
      var show = active() && !st.offer && !st.inView && !st.blocked;
      if (show !== st.shown) {
        st.shown = show;
        bar.classList.toggle('is-shown', show);
        bar.setAttribute('aria-hidden', show ? 'false' : 'true');
        if ('inert' in bar) bar.inert = !show;
        if (btn) btn.tabIndex = show ? 0 : -1;
      }
      setButton(st.card, st.tokens);
      // Reserve the bar's height under the page and KEEP it while the bar is only hidden over the footer, the final CTA,
      // the options or a dialog (no layout jump as those scroll in and out); release it only off phones or back above the trigger.
      if (!active()) O.sticky.pad(0);
      else {
        if (show) bar.__ocPad = Math.ceil(bar.getBoundingClientRect().height); // measured only while shown: no drift
        if (bar.__ocPad) O.sticky.pad(bar.__ocPad);
      }
      if (blockers && blockers.arm) blockers.arm();
    }
    function setButton(card, tokens) {
      if (!btn || !btnLabel) return;
      var label, submit = false, act = 'scroll', aria = '';
      // Every page with an offer form: the button adds the CURRENTLY selected one-time card (default: 3-Bottle Bundle).
      // The subscription card never adds from the bar: it scrolls to its terms and the real button.
      if (form && st.orderable && card && card !== 'subscription') {
        label = fmt(cfg.btnOnce, tokens); submit = !!label; act = submit ? 'add' : 'scroll';
        var inp = form.querySelector('[data-oc-card-input="' + card + '"]');
        if (submit && inp) aria = [inp.getAttribute('data-oc-cta-label'), inp.getAttribute('data-oc-cta-price')].filter(Boolean).join(', ');
      } else if (form && card === 'subscription') { label = cfg.btnRefill; act = 'review'; } // never adds: the terms sit beside the real button
      else label = cfg.btnHome;
      btnLabel.textContent = label || cfg.btnHome || '';
      if (aria) btn.setAttribute('aria-label', decode(aria)); else btn.removeAttribute('aria-label');
      if (submit && form) { btn.type = 'submit'; btn.setAttribute('form', form.getAttribute('id')); }
      else { btn.type = 'button'; btn.removeAttribute('form'); }
      btn.setAttribute('data-oc-action', act);
    }
    function onVariant(e) {
      var det = e.detail || {};
      if (!form || det.form !== form) return;
      st.card = det.card; st.orderable = det.orderable !== false; st.tokens = det.tokens || {};
      var t = cfg.line2 && cfg.line2[det.card];
      var txt = t ? fmt(t, st.tokens) : null;
      if (line2) { line2.textContent = txt || ''; line2.hidden = !txt; }
      if (deal) {
        var tk = st.tokens, sub = det.card === 'subscription';
        var price = sub ? (tk.plan_price || '') : (tk.price || '');
        // The strike-through is the real price of 3 single bottles today, shown only with a real saving (never typed).
        var was = det.card !== 'single' && tk.singles_total && tk.save_amount ? tk.singles_total : '';
        var save = was && cfg.save ? fmt(cfg.save, tk) : null;
        if (priceEl) priceEl.textContent = price;
        if (intEl) { intEl.textContent = sub ? (tk.interval || '') : ''; intEl.hidden = !(sub && tk.interval); }
        if (wasEl) { if (wasV) wasV.textContent = was; wasEl.hidden = !was; }
        if (saveEl) { saveEl.textContent = save || ''; saveEl.hidden = !save; }
        deal.hidden = !price;
      }
      render();
    }
    offs.push(O.on('oc:variant-change', onVariant));
    function onClick(e) {
      var act = btn.getAttribute('data-oc-action');
      if (act === 'add') return; // native submit of the main form (same handler, same checks)
      e.preventDefault();
      if (act === 'review') {
        var terms = form && form.querySelector('[data-oc-disclosure]');
        var main = form && form.querySelector('[data-oc-offer-cta]');
        O.scroll.to(terms || main, { block: 'center' });
        if (main && main.tagName === 'BUTTON') setTimeout(function () { try { main.focus({ preventScroll: true }); } catch (x) { main.focus(); } }, reduced() ? 0 : 450);
        return;
      }
      var target = form ? (form.closest('[data-oc-offer]') || form) : d.getElementById(cfg.target || 'offer');
      if (!target) target = d.getElementById('offer');
      if (!target) return;
      O.scroll.to(target);
      var r = target.querySelector && target.querySelector('[data-oc-card-input]:checked');
      if (r) setTimeout(function () { try { r.focus({ preventScroll: true }); } catch (x) { r.focus(); } }, reduced() ? 0 : 450);
    }
    if (btn) { btn.addEventListener('click', onClick); offs.push(function () { btn.removeEventListener('click', onClick); }); }

    // Triggers. PDP: the main button (buy phase) and the gallery/title block (early phase). Home: the first section.
    var after = mode === 'pdp' ? form.querySelector('[data-oc-offer-cta]') : qs(cfg.after);
    if (!after) after = d.querySelector('#MainContent > .shopify-section') || d.querySelector('main > *');
    var early = mode === 'pdp' ? (d.querySelector('.oc-pdp__media') || d.querySelector('.oc-pdp__head')) : null;
    offs.push(O.sticky.watch(after, function (past) { st.past = past; render(); }));
    if (early) offs.push(O.sticky.watch(early, function (past) { st.early = past; render(); }));
    // An instant jump (anchor link, fast fling) can carry a target from below the viewport to above it without ever
    // intersecting, and IntersectionObserver then never fires. Re-check on scroll (captured, so Horizon's .page-wrapper
    // scroller counts too), once per frame, passive.
    var ticking = false;
    function onScroll() {
      if (ticking || !after) return;
      ticking = true;
      requestAnimationFrame(function () {
        ticking = false;
        var past = after.getBoundingClientRect().bottom < 0;
        var ear = early ? early.getBoundingClientRect().bottom < 0 : false;
        if (past !== st.past || ear !== st.early) { st.past = past; st.early = ear; render(); }
      });
    }
    d.addEventListener('scroll', onScroll, { capture: true, passive: true });
    offs.push(function () { d.removeEventListener('scroll', onScroll, { capture: true }); });
    // Hide over the options themselves (cards + button), the final CTA and the footer (merchant selectors validated).
    if (form) {
      var offerEls = [form.querySelector('.oc-offer__cards'), form.querySelector('.oc-offer__buy')].filter(Boolean);
      if (mode === 'home') offerEls.push(d.getElementById('offer'));
      offs.push(O.sticky.watchInView(offerEls, function (v) { st.offer = v; render(); }));
    } else {
      offs.push(O.sticky.watchInView('#offer', function (v) { st.offer = v; render(); }));
    }
    var hideSel = validSel(cfg.hide, '') || '[data-oc-sticky-hide]';
    offs.push(O.sticky.watchInView(hideSel, function (v) { st.inView = v; render(); }));
    var blockers = null;
    blockers = O.sticky.watchBlockers(function (b) { if (b !== st.blocked) { st.blocked = b; render(); } }, active);
    offs.push(blockers);
    function onMq() { render(); }
    if (mq.addEventListener) { mq.addEventListener('change', onMq); offs.push(function () { mq.removeEventListener('change', onMq); }); }
    else if (mq.addListener) { mq.addListener(onMq); offs.push(function () { mq.removeListener(onMq); }); }
    if (form && form.closest('[data-oc-offer]') && form.closest('[data-oc-offer]').__ocOffer) {
      // the offer initialised before the bar: ask it for the current state
      var r0 = form.closest('[data-oc-offer]');
      if (r0.__ocSelect) r0.__ocSelect(r0.getAttribute('data-oc-sel') || '', false);
    }
    render();
    // Theme editor: a reloaded/removed section must not leave listeners, observers or a stale form behind.
    bar.__ocOff = function () {
      offs.forEach(function (f) { try { if (typeof f === 'function') f(); } catch (x) { /* already gone */ } });
      offs = [];
      O.sticky.pad(0);
      bar.__ocSticky = false;
    };
  }

  /* ---------- cart page: quantity and remove via /cart/change.js + section re-render ---------- */
  function initCart(rootEl) {
    if (rootEl.__ocCart) return;
    var O = OCx();
    if (!O) return;
    rootEl.__ocCart = true;
    var sid = rootEl.getAttribute('data-oc-section-id');
    var busy = false;
    function errBox() { return rootEl.querySelector('[data-oc-cart-error]'); }
    function change(line, qty, key) {
      if (busy || !(line > 0)) return;
      busy = true;
      rootEl.setAttribute('aria-busy', 'true');
      var body = { line: line, quantity: Math.max(0, qty) };
      if (sid) { body.sections = [sid]; body.sections_url = window.location.pathname; }
      O.cart.change(body).then(function (c) {
        var html = c && c.sections && sid ? c.sections[sid] : null;
        if (!html) { window.location.reload(); return; }
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('[data-oc-cart]');
        if (!fresh) { window.location.reload(); return; }
        rootEl.innerHTML = fresh.innerHTML;
        busy = false;
        rootEl.removeAttribute('aria-busy');
        var f = key ? rootEl.querySelector('[data-oc-focus="' + key + '"]') : null;
        if (!f || f.disabled) f = rootEl.querySelector('[data-oc-cart-title]');
        if (f) { if (!f.hasAttribute('tabindex') && !/^(A|BUTTON|INPUT)$/.test(f.tagName)) f.setAttribute('tabindex', '-1'); try { f.focus({ preventScroll: true }); } catch (x) { f.focus(); } }
        announce(rootEl.getAttribute('data-oc-updated') || '');
      }).catch(function (er) {
        busy = false;
        rootEl.removeAttribute('aria-busy');
        var b = errBox();
        if (b) {
          // Shopify's own reason (e.g. "You can only add 2 …") when it refused the change, else the section's message
          var span = b.querySelector('span');
          if (span) {
            if (span.__ocDefault == null) span.__ocDefault = span.textContent;
            var msg = er && er.status === 422 && er.message ? String(er.message) : '';
            span.textContent = (msg && !STOCK_RE.test(msg)) ? msg : span.__ocDefault; // never a stock count
          }
          b.hidden = false;
        }
      });
    }
    rootEl.addEventListener('click', function (e) {
      var a = e.target.closest && e.target.closest('[data-oc-qty]');
      if (!a || !rootEl.contains(a)) return;
      e.preventDefault();
      change(Number(a.getAttribute('data-oc-line')), Number(a.getAttribute('data-oc-qty')), a.getAttribute('data-oc-focus'));
    });
    rootEl.addEventListener('change', function (e) {
      var i = e.target.closest && e.target.closest('[data-oc-qty-input]');
      if (!i) return;
      var q = parseInt(i.value, 10);
      if (isNaN(q) || q < 0) q = 0;
      change(Number(i.getAttribute('data-oc-line')), q, i.getAttribute('data-oc-focus'));
    });
    rootEl.addEventListener('keydown', function (e) {
      var i = e.target.closest && e.target.closest('[data-oc-qty-input]');
      if (i && e.key === 'Enter') { e.preventDefault(); i.blur(); }
    });
  }

  function scan(scope) {
    scope = scope || d;
    $$('[data-oc-offer]', scope).forEach(initOffer);
    $$('[data-oc-gallery]', scope).forEach(initGallery);
    $$('[data-oc-cart]', scope).forEach(initCart);
    $$('[data-oc-sticky]', scope).forEach(initSticky);
  }

  function boot() {
    api.booted = true;
    var start = function () {
      d.addEventListener('oc:variant-change', variantImages);
      scan(d);
      d.addEventListener('shopify:section:load', function (e) {
        // the sticky bar binds to the offer form: rebuild it when either section reloads
        $$('[data-oc-sticky]').forEach(function (b) { if (b.__ocOff && !e.target.contains(b)) { b.__ocOff(); } });
        scan(d);
      });
      d.addEventListener('shopify:section:unload', function (e) {
        $$('[data-oc-sticky]', e.target).forEach(function (b) { if (b.__ocOff) b.__ocOff(); });
        $$('[data-oc-offer]', e.target).forEach(function (r) { if (r.__ocOff) r.__ocOff(); });
      });
    };
    if (window.OC && window.OC.ready) window.OC.ready(start);
    else (window.OCQ = window.OCQ || []).push(start);
  }

  var api = {
    version: '1.1.0', booted: false, CARDS: CARDS,
    pct: pct, perBottle: perBottle, parseInterval: parseInterval, fmt: fmt, compute: compute, planInputValue: planInputValue,
    boot: boot, scan: scan
  };
  return api;
});
